import React, { useState } from "react";
import { BookOpen, Edit2, RotateCcw, ZoomIn, ZoomOut } from "lucide-react";
import RecipeImageEditor from "./RecipeImageEditor";

export interface RecipeDocument {
  sourceName: string;
  version: string;
  bookMealName: string;
  pages: { pageNumber: number; imageUrl: string; updatedAt?: string }[];
  pdfUrl?: string;
  preparationPages?: { pageNumber: number; imageUrl: string; updatedAt?: string }[];
  preparationPdfUrl?: string;
}

const RecipePage: React.FC<{ url: string; label: string }> = ({ url, label }) => {
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  return failed ? (
    <div role="alert" className="p-8 text-center text-sm text-red-700">
      <p>{label} could not be loaded.</p>
      <button className="mt-3 underline" onClick={() => { setFailed(false); setAttempt(attempt + 1); }}>Retry</button>
    </div>
  ) : <img key={attempt} src={url} alt={label} onError={() => setFailed(true)} className="block w-full h-auto bg-white" />;
};

const MealRecipeViewer: React.FC<{ recipe?: RecipeDocument; mealName: string; mealId?: string; canEdit?: boolean; onSaved?: (recipe: RecipeDocument) => void }> = ({ recipe, mealName, mealId, canEdit, onSaved }) => {
  const [zoom, setZoom] = useState(100);
  const [section, setSection] = useState("recipe");
  const [editingPage, setEditingPage] = useState<number | null>(null);
  const editor = editingPage !== null && mealId && onSaved ? <RecipeImageEditor mealId={mealId} pageNumber={editingPage}
    currentUrl={recipe?.pages.find(p => p.pageNumber === editingPage)?.imageUrl || null} onClose={() => setEditingPage(null)} onSaved={onSaved} /> : null;
  if (!recipe?.pages?.length) return (
    <div className="py-12 text-center text-gray-500">
      <BookOpen className="w-8 h-8 mx-auto mb-3" />
      <p className="text-sm">No meal-book recipe linked to this meal.</p>
      {canEdit && mealId && onSaved && <button onClick={() => setEditingPage(1)} className="inline-flex items-center gap-2 mt-4 text-sm text-green-800"><Edit2 size={16} />Add Recipe Image</button>}
      {editor}
    </div>
  );
  const pages = section === "recipe" ? recipe.pages : recipe.preparationPages || [];
  const iconClass = "p-2 hover:bg-gray-100 rounded disabled:opacity-30";
  return (
    <section aria-label="Meal book recipe" className="min-w-0">
      <div className="flex flex-wrap items-center justify-between gap-3 pb-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-gray-800 break-words">{recipe.bookMealName}</p>
          <p className="text-xs text-gray-500 break-words">{recipe.sourceName} ({recipe.version})</p>
        </div>
        <div className="flex flex-wrap items-center gap-1">
          {!!recipe.preparationPages?.length && <select aria-label="Recipe section" value={section} onChange={e => { setSection(e.target.value); setZoom(100); }} className="text-sm border border-gray-300 rounded px-2 py-2 mr-2">
            <option value="recipe">Meal Recipe</option>
            <option value="preparation">Base Preps &amp; Dressings</option>
          </select>}
          <button title="Zoom out" aria-label="Zoom out" className={iconClass} disabled={zoom <= 50} onClick={() => setZoom(Math.max(50, zoom - 25))}><ZoomOut size={18} /></button>
          <span className="w-12 text-center text-xs tabular-nums">{zoom}%</span>
          <button title="Zoom in" aria-label="Zoom in" className={iconClass} disabled={zoom >= 250} onClick={() => setZoom(Math.min(250, zoom + 25))}><ZoomIn size={18} /></button>
          <button title="Fit to width" aria-label="Fit to width" className={iconClass} onClick={() => setZoom(100)}><RotateCcw size={18} /></button>
        </div>
      </div>
      <div className="overflow-auto border border-gray-300 bg-gray-200 max-h-[65vh]">
        <div style={{ width: `${zoom}%` }} className={zoom < 100 ? "mx-auto" : ""}>
          {pages.map(page => <figure key={page.pageNumber} className="m-0 mb-3 last:mb-0">
            <figcaption className="flex items-center justify-between gap-2 text-xs text-gray-600 px-3 py-2 bg-gray-100">
              <span className="min-w-0 break-words">Page {page.pageNumber}{page.updatedAt ? " - Updated " + new Date(page.updatedAt).toLocaleDateString() : ""}</span>
              {section === "recipe" && canEdit && mealId && onSaved && <button title="Update recipe image" aria-label={`Update recipe image page ${page.pageNumber}`} onClick={() => setEditingPage(page.pageNumber)} className={`${iconClass} shrink-0`}><Edit2 size={16} /></button>}
            </figcaption>
            <RecipePage key={page.imageUrl} url={page.imageUrl} label={`${recipe.bookMealName || mealName} - meal book page ${page.pageNumber}`} />
          </figure>)}
        </div>
      </div>
      {editor}
    </section>
  );
};

export default MealRecipeViewer;
